<template>
  <div class="edit-main">
    <!-- 顶部 -->
    <div class="top-bar">
      <Button @click="GoBack" type="primary">
        <Icon type="ios-arrow-back" />返回
      </Button>
      <span class="title">{{ hopeEvaluationId ? '编辑试卷' : '新建试卷' }}</span>
      <div class="actions">
        <Button type="default" @click="OnGenerate" :loading="generating" style="margin-right:8px;">生成预览</Button>
        <Button type="primary" @click="OnSave" :loading="saving">保存试卷</Button>
      </div>
    </div>

    <!-- 顶部表单（基础信息） -->
    <div class="base-form">
      <div class="row">
        <div class="label"><span>*</span>归属老师</div>
        <Select v-model="teacherId" placeholder="请选择" style="width:220px;">
          <Option
            v-for="t in teacherList"
            :key="t.uid || t.id"
            :value="t.uid || t.id"
          >{{ t.name || t.nickname }}</Option>
        </Select>
      </div>
      <div class="row">
        <div class="label"><span>*</span>试卷名称</div>
        <Input v-model="evaluationName" placeholder="请输入试卷名称" style="width:280px;"/>
      </div>
      <div class="row">
        <div class="label"><span>*</span>总答题时长（分钟）</div>
        <InputNumber :min="1" :step="1" v-model="totalTimeMinutes" style="width:140px;"/>
      </div>
      <div class="row">
        <div class="label">评分严格度</div>
        <Select v-model="scoreElasticity" placeholder="请选择" style="width:140px;">
          <Option
            v-for="opt in scoreElasticityOpts"
            :key="opt.id"
            :value="String(opt.id)"
          >{{ opt.name }}</Option>
        </Select>
      </div>
      <div class="row">
        <div class="label">测评报告</div>
        <Select v-model="isShowReport" placeholder="请选择" style="width:140px;">
          <Option :value="1">显示</Option>
          <Option :value="0">不显示</Option>
        </Select>
      </div>
      <div class="row">
        <div class="label">试卷总分</div>
        <span class="total-score">{{ totalScore }} <i>分</i></span>
      </div>
    </div>

    <!-- 左右分栏 -->
    <div class="split">
      <!-- 左：手机预览 -->
      <div class="left">
        <div class="phone">
          <div class="phone-header">
            <div class="phone-title">
              第{{ currentIndex + 1 }}题 · {{ currentBlockName }}
            </div>
            <div class="phone-timer">{{ FormatSecond(evaluationTotalTime) }}</div>
          </div>
          <div class="phone-body">
            <div v-if="!currentQuestion" class="empty-tip">
              请在右侧配置后，点击"生成预览"
            </div>
            <div v-else class="q-content">
              <!-- 题型介绍 -->
              <div class="q-intro" v-if="currentBlockDesc">
                {{ currentBlockDesc }}
              </div>
              <div class="q-intro-audio" v-if="currentBlockIntroAudio">
                <audio :src="currentBlockIntroAudio" controls/>
              </div>

              <!-- 题目主体 -->
              <div class="q-text" v-if="currentQuestion.word">
                <b>{{ currentQuestion.word }}</b>
                <span v-if="currentQuestion.meaning" class="meaning"> （{{ currentQuestion.meaning }}）</span>
              </div>
              <div class="q-pic" v-if="currentQuestion.pic">
                <img :src="currentQuestion.pic" />
              </div>
              <div class="q-text" v-if="currentQuestion.sentence">
                {{ currentQuestion.sentence }}
              </div>
              <div class="q-text" v-if="currentQuestion.question">
                {{ currentQuestion.question }}
              </div>
              <div class="q-answer" v-if="currentQuestion.answer">
                参考答案：{{ currentQuestion.answer }}
              </div>
              <div class="q-dialogue" v-if="currentQuestion.dialogue && currentQuestion.dialogue.length">
                <div
                  v-for="(d, di) in currentQuestion.dialogue"
                  :key="di"
                  class="d-row"
                  :class="{ 'd-bot': d.is_bot == 1, 'd-user': d.is_bot == 0 }"
                >
                  <span class="d-role">{{ d.is_bot == 1 ? '机器' : '我' }}</span>
                  <span class="d-text">{{ d.sentence }}</span>
                </div>
              </div>

              <!-- 题目音频（如果题目本身有，比如情景问答的题干音频） -->
              <div class="q-audio" v-if="currentQuestion.audio">
                <audio :src="currentQuestion.audio" controls/>
              </div>

              <div class="q-meta">
                <span>分值：{{ currentScore || 0 }} 分</span>
                <span v-if="currentDuration" style="margin-left:12px;">时长：{{ currentDuration }}s</span>
                <span v-if="currentDifficult" style="margin-left:12px;">难度：{{ GetDifficultName(currentBlockTypeId, currentDifficult) }}</span>
              </div>
            </div>
          </div>
          <div class="phone-footer" v-if="flatQuestions.length">
            <Button size="small" @click="PrevQ" :disabled="currentIndex <= 0">上一题</Button>
            <Button size="small" type="warning" @click="OnChangeQuestion" style="margin:0 8px;">换一题</Button>
            <Button size="small" @click="NextQ" :disabled="currentIndex >= flatQuestions.length - 1">下一题</Button>
          </div>
          <div class="phone-progress" v-if="flatQuestions.length">
            {{ currentIndex + 1 }} / {{ flatQuestions.length }}
          </div>
        </div>
      </div>

      <!-- 右：配置 -->
      <div class="right">
        <div
          v-for="(block, idx) in selectInfo"
          :key="idx"
          class="type-block"
        >
          <div class="type-title">{{ GetTypeName(block.type_id) }}</div>
          <div class="form-row">
            <div class="form-cell">
              <div class="cell-label">题目数量</div>
              <InputNumber :min="0" v-model="block.num" @on-change="val => OnNumChange(block, val)" style="width:100%;"/>
            </div>
            <div class="form-cell">
              <div class="cell-label">题目分数</div>
              <InputNumber :min="0" :max="50" :step="1" v-model="block.score" @on-change="val => OnScoreChange(block, val)" style="width:100%;"/>
            </div>
            <div class="form-cell">
              <div class="cell-label">题目难度</div>
              <Select v-model="block.level" placeholder="请选择" style="width:100%;">
                <Option
                  v-for="d in GetDifficults(block.type_id)"
                  :key="d.id"
                  :value="String(d.id)"
                >{{ d.name }}</Option>
              </Select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-cell">
              <div class="cell-label">教材</div>
              <Select v-model="block.book_id" placeholder="请选择" style="width:100%;" @on-change="OnBookChange(block)">
                <Option
                  v-for="b in GetBooks(block.type_id)"
                  :key="b.book_id"
                  :value="b.book_id"
                >{{ b.title }}</Option>
              </Select>
            </div>
            <div class="form-cell">
              <div class="cell-label">单元</div>
              <Select v-model="block.chapter_id" placeholder="请选择" style="width:100%;">
                <Option
                  v-for="u in GetChapters(block.type_id, block.book_id)"
                  :key="u.id"
                  :value="u.id"
                >{{ u.title }}</Option>
              </Select>
            </div>
            <div class="form-cell">
              <div class="cell-label">单题固定时长（秒）</div>
              <InputNumber :min="1" :max="100" v-model="block.duration" @on-change="val => OnDurationChange(block, val)" style="width:100%;"/>
            </div>
          </div>
        </div>

        <div v-if="!selectInfo.length" class="no-type-tip">
          正在加载题型...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import evaluationApi from '@/lib/api/evaluation'
import schoolApi from '@/lib/api/school'

export default {
  name: 'EvaluationEdit',
  data() {
    return {
      hopeEvaluationId: '',
      teacherId: '',
      evaluationName: '',
      evaluationTotalTime: 3600,
      scoreElasticity: '1',
      scoreElasticityOpts: [],
      isManual: 2,
      isShowReport: 1,
      typeInfo: [],
      questionInfo: [],
      teacherList: [],
      selectInfo: [],
      groups: [],
      flatQuestions: [],
      currentIndex: 0,
      historyMap: {},
      generating: false,
      saving: false,
      // 总分超过100时是否已弹过提示（用户点“继续”后置为 true，回落到≤100时重置）
      overScoreConfirmed: false
    }
  },
  computed: {
    // 总答题时长（分钟）：与内部按秒存储的 evaluationTotalTime 互转
    totalTimeMinutes: {
      get() {
        return Math.round((Number(this.evaluationTotalTime) || 0) / 60)
      },
      set(val) {
        this.evaluationTotalTime = (Number(val) || 0) * 60
      }
    },
    // 试卷总分 = 各题型块（题目数量 × 单题分数）之和
    totalScore() {
      return (this.selectInfo || []).reduce((sum, b) => {
        const num = Number(b.num) || 0
        const score = Number(b.score) || 0
        return sum + num * score
      }, 0)
    },
    // 所有题型块的题目总数
    totalNum() {
      return (this.selectInfo || []).reduce((sum, b) => sum + (Number(b.num) || 0), 0)
    },
    currentItem() {
      return this.flatQuestions[this.currentIndex]
    },
    currentQuestion() {
      return this.currentItem && this.currentItem.question
    },
    currentBlockTypeId() {
      return this.currentItem && this.currentItem.typeId
    },
    currentBlockConfig() {
      if (!this.currentItem) return null
      return this.selectInfo.find(b => b.type_id == this.currentItem.typeId) || null
    },
    currentScore() {
      const cfg = this.currentBlockConfig
      if (cfg && cfg.score != null && cfg.score !== '') return cfg.score
      return (this.currentQuestion && this.currentQuestion.score) || 0
    },
    currentDuration() {
      const cfg = this.currentBlockConfig
      if (cfg && cfg.duration != null && cfg.duration !== '') return cfg.duration
      return this.currentQuestion && this.currentQuestion.second
    },
    currentDifficult() {
      const cfg = this.currentBlockConfig
      if (cfg && cfg.level != null && cfg.level !== '') return cfg.level
      return this.currentQuestion && this.currentQuestion.difficult
    },
    currentBlockName() {
      if (!this.currentItem) return ''
      return this.currentItem.blockTitle || this.GetTypeName(this.currentItem.typeId)
    },
    currentBlockDesc() {
      return this.currentItem && this.currentItem.blockDesc
    },
    currentBlockIntroAudio() {
      return this.currentItem && this.currentItem.blockAudio
    }
  },
  watch: {
    totalScore(newVal, oldVal) {
      if (newVal <= 100) {
        // 回落到 100 以内，重置标记，下次再超才提示
        this.overScoreConfirmed = false
        return
      }
      // 已超过 100：仅在首次跨过阈值时提示一次
      if (oldVal <= 100 && !this.overScoreConfirmed) {
        this.PromptOverScore()
      }
    }
  },
  created() {
    this.hopeEvaluationId = this.$route.query.hope_evaluation_id || ''
    const tid = this.$route.query.teacher_id
    if (tid) this.teacherId = Number(tid)
    this.LoadTeachers()
    this.LoadConfig()
  },
  methods: {
    // 所有题型题目总数不得超过50，超过时回正当前块
    OnNumChange(block, val) {
      const cur = Number(val) || 0
      // 其它题型块的题目数之和
      const otherSum = this.totalNum - cur
      const allowed = 50 - otherSum
      if (cur > allowed) {
        this.$Message.warning('题目数量最大为50')
        this.$set(block, 'num', allowed < 0 ? 0 : allowed)
      }
    },
    // 单题分数超过50时提示并回正
    OnScoreChange(block, val) {
      if (Number(val) > 50) {
        this.$Message.warning('单题分数最大为50分')
        this.$set(block, 'score', 50)
      }
    },
    // 单题固定时长超过100秒时提示并回正
    OnDurationChange(block, val) {
      if (Number(val) > 100) {
        this.$Message.warning('单题最大答题时长为100秒')
        this.$set(block, 'duration', 100)
      }
    },
    GoBack() {
      this.$Modal.confirm({
        title: '离开提示',
        content: '是否保存当前试卷？未保存的修改将会丢失。',
        okText: '保存',
        cancelText: '不保存',
        onOk: () => {
          this.OnSave()
        },
        onCancel: () => {
          this.$router.push({ name: 'evaluationList' })
        }
      })
    },
    // 总分超过100时提示：继续编辑 or 弹保存
    PromptOverScore() {
      this.$Modal.confirm({
        title: '分值提示',
        content: `当前试卷总分已达 ${this.totalScore} 分，超过 100 分。是否继续编辑？`,
        okText: '继续编辑',
        cancelText: '否',
        onOk: () => {
          // 允许继续编辑，本次超额不再重复提示
          this.overScoreConfirmed = true
        },
        onCancel: () => {
          // 不允许，弹是否保存
          this.$Modal.confirm({
            title: '离开提示',
            content: '是否保存当前试卷？',
            okText: '保存',
            cancelText: '不保存',
            onOk: () => {
              this.OnSave()
            },
            onCancel: () => {
              // 不保存：停留在当前编辑页，不做任何跳转
            }
          })
        }
      })
    },
    LoadTeachers() {
      schoolApi.GetAdminList({
        keyword: '',
        start: 0,
        rows: 100,
        type: 1
      }).then(res => {
        if (res.status == 1) {
          this.teacherList = res.data.list || []
        }
      })
    },
    LoadConfig() {
      evaluationApi.GetConfigInfo({
        hopeEvaluationId: this.hopeEvaluationId
      }).then(res => {
        if (res.status != 1) {
          this.$Message.error(res.msg || '获取配置失败')
          return
        }
        const data = res.data || {}
        this.typeInfo = data.question_type_info || data.question_type_indfo || []
        this.questionInfo = data.question_info || []
        this.scoreElasticityOpts = data.score_elasticity || []

        const existed = data.exist_evaluation_config_info
        if (this.hopeEvaluationId && existed) {
          this.evaluationName = existed.evaluation_name || ''
          this.evaluationTotalTime = Number(existed.evaluation_total_time) || 3600
          this.scoreElasticity = String(existed.score_elasticity || '0')
          // 录制模式已下线，恒为手动录音（isManual=2），不再读取后端旧值
          if (existed.is_show_report !== undefined && existed.is_show_report !== null && existed.is_show_report !== '') {
            this.isShowReport = Number(existed.is_show_report)
          }
          this.selectInfo = this.ParseSelectInfo(existed.select_info)
          this.RefreshPreview()
        } else {
          this.selectInfo = this.BuildDefaultSelectInfo()
        }
      })
    },
    ParseSelectInfo(raw) {
      if (!raw) return this.BuildDefaultSelectInfo()
      try {
        const arr = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (Array.isArray(arr) && arr.length) {
          // 兼容老数据：unit_id -> chapter_id
          arr.forEach(b => {
            if (b && b.unit_id != null && b.chapter_id == null) {
              b.chapter_id = b.unit_id
              delete b.unit_id
            }
          })
          return arr
        }
      } catch (e) { /* fallthrough */ }
      return this.BuildDefaultSelectInfo()
    },
    BuildDefaultSelectInfo() {
      return (this.typeInfo || []).map(t => {
        const qi = (this.questionInfo || []).find(x => x.type_id == t.id) || {}
        const diffs = qi.difficult || []
        const books = qi.books || []
        const firstBook = books[0] || {}
        const firstChapter = (firstBook.chapters || [])[0] || {}
        return {
          type_id: t.id,
          num: 1,
          score: 5,
          level: diffs.length ? String(diffs[0].id) : '',
          book_id: firstBook.book_id || '',
          chapter_id: firstChapter.id || '',
          duration: Number(qi.second) || 30
        }
      })
    },
    GetTypeName(typeId) {
      const t = (this.typeInfo || []).find(x => x.id == typeId)
      return t ? t.title : ('题型' + typeId)
    },
    GetTypeMeta(typeId) {
      return (this.questionInfo || []).find(x => x.type_id == typeId) || {}
    },
    GetDifficults(typeId) {
      return this.GetTypeMeta(typeId).difficult || []
    },
    GetBooks(typeId) {
      return this.GetTypeMeta(typeId).books || []
    },
    GetChapters(typeId, bookId) {
      const books = this.GetBooks(typeId)
      const b = books.find(x => x.book_id == bookId)
      return b ? (b.chapters || []) : []
    },
    OnBookChange(block) {
      block.chapter_id = ''
    },
    Validate() {
      if (!this.teacherId) { this.$Message.error('请选择归属老师'); return false }
      if (!this.evaluationName) { this.$Message.error('请输入试卷名称'); return false }
      if (!this.evaluationTotalTime) { this.$Message.error('请输入总答题时长'); return false }
      for (let i = 0; i < this.selectInfo.length; i++) {
        const b = this.selectInfo[i]
        if (!b.num || !b.score || !b.book_id || !b.chapter_id || !b.duration) {
          this.$Message.error(`第 ${i + 1} 个题型块（${this.GetTypeName(b.type_id)}）配置不完整`)
          return false
        }
      }
      return true
    },
    OnGenerate() {
      if (!this.Validate()) return
      this.generating = true
      evaluationApi.GetNewEvaluationData({
        teacherId: this.teacherId,
        evaluationName: this.evaluationName,
        evaluationTotalTime: String(this.evaluationTotalTime),
        scoreElasticity: this.scoreElasticity,
        selectInfo: this.selectInfo,
        hopeEvaluationId: this.hopeEvaluationId
      }).then(res => {
        this.generating = false
        if (res.status == 1) {
          this.$Message.success('生成成功')
          this.SetGroupsFromResponse(res.data)
        } else {
          this.$Message.error(res.msg || '生成失败')
        }
      }).catch(() => { this.generating = false })
    },
    RefreshPreview() {
      // 编辑场景：进入页面时拉一次完整试卷题目
      if (!this.hopeEvaluationId) return
      evaluationApi.GetPreviewEvaluationData({
        hopeEvaluationId: this.hopeEvaluationId
      }).then(res => {
        if (res.status == 1) {
          this.SetGroupsFromResponse(res.data)
        }
      })
    },
    SetGroupsFromResponse(data) {
      console.log('[evaluation] response data =', JSON.stringify(data, null, 2))
      const groups = (data && (data.question_type || data.question_list || data.list || data.groups)) || []
      this.groups = groups
      const flat = []
      groups.forEach(g => {
        const typeId = g.type_id
        const qs = g.question || g.question_info || g.questions || []
        qs.forEach(q => flat.push({
          typeId,
          blockTitle: g.title,
          blockDesc: g.desc,
          blockAudio: g.audio,
          question: q
        }))
      })
      this.flatQuestions = flat
      this.currentIndex = 0
    },
    GetDifficultName(typeId, diffId) {
      const list = this.GetDifficults(typeId)
      const item = list.find(x => x.id == diffId)
      return item ? item.name : diffId
    },
    PrevQ() { if (this.currentIndex > 0) this.currentIndex-- },
    NextQ() { if (this.currentIndex < this.flatQuestions.length - 1) this.currentIndex++ },
    OnChangeQuestion() {
      const cur = this.flatQuestions[this.currentIndex]
      if (!cur) return
      const typeId = cur.typeId
      const block = this.selectInfo.find(b => b.type_id == typeId)
      if (!block) return
      const qid = cur.question.question_id || cur.question.id
      const historyKey = `${typeId}`
      this.historyMap[historyKey] = this.historyMap[historyKey] || []
      if (qid && !this.historyMap[historyKey].includes(qid)) {
        this.historyMap[historyKey].push(qid)
      }
      evaluationApi.ChangeQuestion({
        selectInfo: this.selectInfo,
        currentQuestionId: qid,
        historyId: this.historyMap[historyKey].join(',')
      }).then(res => {
        if (res.status == 1) {
          const newQ = res.data && (res.data.question || res.data)
          if (newQ) {
            this.$set(this.flatQuestions, this.currentIndex, { typeId, question: newQ })
          }
        } else {
          this.$Message.error(res.msg || '换题失败')
        }
      })
    },
    BuildSaveInfo() {
      const map = {}
      this.flatQuestions.forEach(item => {
        const t = item.typeId
        if (!map[t]) map[t] = []
        const q = item.question
        map[t].push({
          question_id: q.question_id || q.id,
          score: q.score
        })
      })
      return Object.keys(map).map(t => ({
        type_id: Number(t),
        question_info: map[t]
      }))
    },
    OnSave() {
      if (!this.Validate()) return
      if (!this.flatQuestions.length) {
        this.$Message.error('请先点击"生成预览"生成题目')
        return
      }
      const saveInfo = this.BuildSaveInfo()
      this.saving = true
      evaluationApi.SaveEvaluation({
        teacherId: this.teacherId,
        evaluationName: this.evaluationName,
        evaluationTotalTime: String(this.evaluationTotalTime),
        scoreElasticity: this.scoreElasticity,
        selectInfo: this.selectInfo,
        saveInfo,
        hopeEvaluationId: this.hopeEvaluationId,
        isManual: this.isManual,
        isShowReport: this.isShowReport
      }).then(res => {
        this.saving = false
        if (res.status == 1) {
          this.$Message.success('保存成功')
          this.$router.push({ name: 'evaluationList' })
        } else {
          this.$Message.error(res.msg || '保存失败')
        }
      }).catch(() => { this.saving = false })
    },
    FormatSecond(sec) {
      const s = Number(sec) || 0
      const m = Math.floor(s / 60)
      const r = s % 60
      const mm = m < 10 ? '0' + m : m
      const ss = r < 10 ? '0' + r : r
      return `${mm}:${ss}`
    }
  }
}
</script>

<style lang="less" scoped>
.edit-main {
  width: 100%;
  height: 100%;
  padding: 18px;
  box-sizing: border-box;
  background: #f3f3f3;
  .top-bar {
    width: 100%;
    height: 56px;
    background: #fff;
    padding: 0 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    .title {
      font-size: 16px;
      font-weight: 600;
      margin-left: 16px;
      flex: 1;
    }
    .actions { display: flex; }
  }
  .base-form {
    width: 100%;
    background: #fff;
    padding: 12px 16px;
    border-radius: 8px;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 12px;
    .row {
      display: flex;
      align-items: center;
      margin-right: 32px;
      margin-bottom: 8px;
      .label {
        font-size: 14px;
        color: #333;
        margin-right: 8px;
        span {
          color: #f12121;
          margin-right: 2px;
        }
      }
      .total-score {
        font-size: 20px;
        font-weight: 700;
        color: #ff7a45;
        i {
          font-size: 13px;
          font-weight: 400;
          font-style: normal;
          color: #999;
        }
      }
    }
  }
  .split {
    display: flex;
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    min-height: calc(100vh - 280px);
    .left {
      width: 340px;
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      .phone {
        width: 300px;
        background: linear-gradient(180deg, #ffeae0, #fff);
        border-radius: 24px;
        padding: 20px 16px 16px;
        box-shadow: 0 2px 12px rgba(0,0,0,.08);
        position: relative;
        .phone-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: #fff;
          border-radius: 16px;
          margin-bottom: 12px;
          .phone-title {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
          .phone-timer {
            font-size: 12px;
            color: #ff4d4f;
          }
        }
        .phone-body {
          min-height: 280px;
          max-height: 480px;
          overflow-y: auto;
          background: #fff;
          border-radius: 12px;
          padding: 16px;
          .empty-tip {
            color: #bbb;
            text-align: center;
            padding-top: 100px;
            font-size: 13px;
          }
          .q-content {
            .q-intro {
              font-size: 12px;
              color: #888;
              line-height: 1.5;
              margin-bottom: 8px;
              padding: 8px;
              background: #fafafa;
              border-radius: 6px;
            }
            .q-intro-audio audio {
              width: 100%;
              height: 32px;
              margin-bottom: 10px;
            }
            .q-text {
              font-size: 15px;
              color: #333;
              line-height: 1.6;
              margin-bottom: 10px;
              word-break: break-all;
              b { font-size: 18px; color: #1890ff; }
              .meaning { color: #999; font-size: 13px; }
            }
            .q-pic {
              margin-bottom: 10px;
              img {
                max-width: 100%;
                max-height: 160px;
                border-radius: 6px;
              }
            }
            .q-answer {
              font-size: 13px;
              color: #52c41a;
              margin-bottom: 8px;
              padding: 6px 8px;
              background: #f6ffed;
              border-radius: 4px;
            }
            .q-dialogue {
              margin-bottom: 10px;
              .d-row {
                display: flex;
                align-items: flex-start;
                padding: 6px 0;
                .d-role {
                  display: inline-block;
                  width: 36px;
                  font-size: 11px;
                  color: #999;
                  flex-shrink: 0;
                }
                .d-text {
                  flex: 1;
                  font-size: 13px;
                  line-height: 1.5;
                  padding: 6px 10px;
                  border-radius: 10px;
                }
                &.d-bot .d-text { background: #f0f0f0; color: #333; }
                &.d-user .d-text { background: #e6f7ff; color: #1890ff; }
              }
            }
            .q-audio audio {
              width: 100%;
              height: 36px;
              margin-bottom: 8px;
            }
            .q-meta {
              font-size: 12px;
              color: #ff7a45;
              margin-top: 8px;
            }
          }
        }
        .phone-footer {
          display: flex;
          justify-content: space-between;
          margin-top: 12px;
        }
        .phone-progress {
          text-align: center;
          font-size: 12px;
          color: #999;
          margin-top: 8px;
        }
      }
    }
    .right {
      flex: 1;
      padding-left: 24px;
      overflow-y: auto;
      .type-block {
        background: #fafafa;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
        .type-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #eee;
        }
        .form-row {
          display: flex;
          margin-bottom: 12px;
          &:last-child { margin-bottom: 0; }
          .form-cell {
            flex: 1;
            margin-right: 12px;
            &:last-child { margin-right: 0; }
            .cell-label {
              font-size: 13px;
              color: #666;
              margin-bottom: 6px;
            }
          }
        }
      }
      .no-type-tip {
        text-align: center;
        color: #999;
        padding: 60px 0;
      }
    }
  }
}
</style>
